import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { getPilotageProjets, confirmerJalon, refuserJalon } from "../services/api";
import IconeV2 from "../components/IconeV2";
import ModaleV2 from "../components/ModaleV2";
import ModaleNouveauProjetV2 from "../components/ModaleNouveauProjetV2";
import { VueCollaborateurs, VueListe, JalonsAConfirmer } from "../components/PilotageProjetsBlocsV2";
import { formatHeures } from "../services/projet";

// ===================================================================
// The Crowd v2 — Pilotage › Projets (vue manager)
//
// Refonte du module Projets : les projets de mon equipe (dont je suis
// chef ou co-chef, ou portes par un collaborateur de mon equipe), avec
// avancement, retards, jalons a confirmer et temps passe ; creation et
// affectation. Un directeur peut elargir a tous les projets. Remplace le
// Dashboard projets.
// Vue Cartes : une carte par acteur principal (projets, avancement global,
// jalons, retards, temps), puis une sous-carte par projet. Vue Liste :
// lecture par collaborateur, puis tableau des projets. Choix memorise.
// Blocs d'affichage : components/PilotageProjetsBlocsV2.js
// ===================================================================

const VUE_STORAGE_KEY = "thecrowd-v2-vue-pilotage-projets";

function vueInitiale() {
  try {
    const v = window.localStorage.getItem(VUE_STORAGE_KEY);
    if (v === "cartes" || v === "liste") return v;
  } catch {
    // stockage indisponible : vue par defaut
  }
  return "cartes";
}

function Stat({ libelle, valeur, ton }) {
  return (
    <div className="v2-stat">
      <span className="v2-stat-libelle">{libelle}</span>
      <span className={`v2-stat-valeur${ton ? ` v2-texte-${ton}` : ""}`}>{valeur}</span>
    </div>
  );
}

// --- Modale « Ne pas confirmer » ---

function ModaleRefus({ jalon, onValider, onClose }) {
  const [mot, setMot] = useState("");
  const [enCours, setEnCours] = useState(false);
  const [erreur, setErreur] = useState(null);

  const valider = async () => {
    setEnCours(true);
    setErreur(null);
    try {
      await onValider(mot.trim());
    } catch (err) {
      setErreur(err.message);
      setEnCours(false);
    }
  };

  return (
    <ModaleV2
      titre="Ne pas confirmer le jalon"
      onClose={onClose}
      bloquee={enCours}
      actions={
        <>
          <button type="button" className="v2-btn v2-btn-secondaire" onClick={onClose} disabled={enCours}>Annuler</button>
          <button type="button" className="v2-btn v2-btn-principal" onClick={valider} disabled={enCours || !mot.trim()}>
            {enCours ? "Envoi…" : "Envoyer"}
          </button>
        </>
      }
    >
      {erreur && <div className="v2-msg v2-msg-erreur">{erreur}</div>}
      <p className="v2-champ-aide">« {jalon.intitule} » repasse en cours. Un mot d'explication est demandé pour l'acteur principal.</p>
      <div className="v2-champ">
        <label className="v2-label" htmlFor="refus-mot">Votre mot</label>
        <textarea id="refus-mot" rows={3} value={mot} onChange={(e) => setMot(e.target.value)} />
      </div>
    </ModaleV2>
  );
}

// --- Page ---

function ProjetsPilotageV2({ collaborateur }) {
  const navigate = useNavigate();
  const estDirecteur = collaborateur?.role === "directeur";
  const [donnees, setDonnees] = useState(null);
  const [erreur, setErreur] = useState(null);
  const [message, setMessage] = useState(null);
  const [perimetre, setPerimetre] = useState("equipe");
  const [inclureTermines, setInclureTermines] = useState(false);
  const [vue, setVue] = useState(vueInitiale);
  const [confirmation, setConfirmation] = useState(null); // id du jalon en cours de confirmation
  const [refus, setRefus] = useState(null);
  const [creation, setCreation] = useState(false);

  useEffect(() => {
    try {
      window.localStorage.setItem(VUE_STORAGE_KEY, vue);
    } catch {
      // cf. vueInitiale
    }
  }, [vue]);

  const charger = useCallback(() => {
    if (!collaborateur) return Promise.resolve();
    setErreur(null);
    return getPilotageProjets(collaborateur.id, perimetre, inclureTermines)
      .then(setDonnees)
      .catch((err) => setErreur(`Projets non chargés : ${err.message}`));
  }, [collaborateur, perimetre, inclureTermines]);

  useEffect(() => {
    charger();
  }, [charger]);

  if (!collaborateur) {
    return (
      <div className="v2-page v2-page-module">
        <div className="v2-msg v2-msg-erreur">Compte non configuré. Contactez votre manager.</div>
      </div>
    );
  }

  const ouvrir = (projetId) => navigate(`/v2/projets/${projetId}`);

  const confirmer = async (jalon) => {
    setConfirmation(jalon.id);
    setMessage(null);
    try {
      await confirmerJalon(jalon.id, collaborateur.id);
      setMessage({ type: "succes", texte: `« ${jalon.intitule} » est confirmé : jalon atteint.` });
      await charger();
    } catch (err) {
      setMessage({ type: "erreur", texte: `Confirmation impossible : ${err.message}` });
    } finally {
      setConfirmation(null);
    }
  };

  const refuser = async (mot) => {
    await refuserJalon(refus.id, mot, collaborateur.id);
    setMessage({ type: "succes", texte: `« ${refus.intitule} » repasse en cours, votre mot est transmis.` });
    setRefus(null);
    await charger();
  };

  const projets = donnees?.projets || [];
  const ind = donnees?.indicateurs;

  return (
    <div className="v2-page v2-page-module">
      <div className="v2-bib-entete">
        <header className="v2-module-entete">
          <h1 className="v2-module-titre">Projets</h1>
          <p className="v2-sous-titre">
            Les objectifs de mon équipe : avancement, retards, jalons à confirmer et temps passé.
          </p>
        </header>
        <button type="button" className="v2-btn v2-btn-principal" onClick={() => setCreation(true)}>
          <IconeV2 nom="plus" /> Nouveau projet
        </button>
      </div>

      <div className="v2-barre-outils">
        <div className="v2-pilotage-filtres">
          {estDirecteur && (
            <div className="v2-bascule" role="group" aria-label="Périmètre">
              <button type="button" className={`v2-bascule-choix${perimetre === "equipe" ? " actif" : ""}`} aria-pressed={perimetre === "equipe"} onClick={() => setPerimetre("equipe")}>
                Mon équipe
              </button>
              <button type="button" className={`v2-bascule-choix${perimetre === "tous" ? " actif" : ""}`} aria-pressed={perimetre === "tous"} onClick={() => setPerimetre("tous")}>
                Tous les projets
              </button>
            </div>
          )}
          <label className="v2-case-simple">
            <input type="checkbox" checked={inclureTermines} onChange={(e) => setInclureTermines(e.target.checked)} />
            <span>Inclure terminés et abandonnés</span>
          </label>
        </div>
        <div className="v2-barre-outils-droite">
          <div className="v2-bascule" role="group" aria-label="Affichage">
            <button type="button" className={`v2-bascule-choix${vue === "cartes" ? " actif" : ""}`} aria-pressed={vue === "cartes"} onClick={() => setVue("cartes")}>
              <IconeV2 nom="tableau" taille={13} /> Cartes
            </button>
            <button type="button" className={`v2-bascule-choix${vue === "liste" ? " actif" : ""}`} aria-pressed={vue === "liste"} onClick={() => setVue("liste")}>
              <IconeV2 nom="liste" taille={13} /> Liste
            </button>
          </div>
        </div>
      </div>

      {erreur && <div className="v2-msg v2-msg-erreur v2-marge-bas">{erreur}</div>}
      {message && (
        <div className={`v2-msg ${message.type === "erreur" ? "v2-msg-erreur" : "v2-msg-succes"} v2-marge-bas`} role="status">
          {message.texte}
        </div>
      )}
      {!donnees && !erreur && <div className="v2-chargement">Chargement…</div>}

      {ind && (
        <div className="v2-dash-stats v2-pilotage-stats">
          <Stat libelle="Projets" valeur={ind.projets} />
          <Stat libelle="Projets en retard" valeur={ind.projets_en_retard} ton={ind.projets_en_retard ? "danger" : null} />
          <Stat libelle="Étapes en retard" valeur={ind.etapes_en_retard} ton={ind.etapes_en_retard ? "danger" : null} />
          <Stat libelle="Jalons à confirmer" valeur={ind.jalons_a_confirmer} ton={ind.jalons_a_confirmer ? "warning" : null} />
          <Stat libelle="Jalons atteints" valeur={ind.jalons_atteints} ton={ind.jalons_atteints ? "success" : null} />
          <Stat libelle="Temps passé (étapes)" valeur={formatHeures(ind.heures)} />
        </div>
      )}

      {donnees && donnees.jalons_a_confirmer.length > 0 && (
        <JalonsAConfirmer jalons={donnees.jalons_a_confirmer} onOuvrirProjet={ouvrir}
          onConfirmer={confirmer} onRefuser={setRefus} enCours={confirmation} />
      )}

      {donnees && projets.length === 0 && (
        <div className="v2-panneau"><p className="v2-vide">Aucun projet dans ce périmètre.</p></div>
      )}
      {donnees && projets.length > 0 && vue === "cartes" && (
        <VueCollaborateurs projets={projets} parCollaborateur={donnees.par_collaborateur} onOuvrirProjet={ouvrir} />
      )}
      {donnees && projets.length > 0 && vue === "liste" && (
        <VueListe projets={projets} parCollaborateur={donnees.par_collaborateur} onOuvrirProjet={ouvrir} />
      )}

      {refus && <ModaleRefus jalon={refus} onValider={refuser} onClose={() => setRefus(null)} />}
      {creation && (
        <ModaleNouveauProjetV2
          collaborateur={collaborateur}
          onClose={() => setCreation(false)}
          onCree={(projetId) => { setCreation(false); navigate(`/v2/projets/${projetId}`); }}
        />
      )}
    </div>
  );
}

export default ProjetsPilotageV2;
